/**
 * ForReadersSection — split layout with a reader-app screenshot on one side
 * and a feature bullet list + CTA on the other.
 */

import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';
import { landingTokens as T } from '@codex/theme';

import ExploreIcon from '@mui/icons-material/Explore';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import BookmarksIcon from '@mui/icons-material/Bookmarks';
import VolunteerActivismIcon from '@mui/icons-material/VolunteerActivism';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

import {
  Section,
  Container,
  SectionEyebrow,
  SectionTitle,
  SectionSub,
  ReaderSplit,
  ReaderVisual,
  ReaderFloat,
  ReaderFeatureList,
  ReaderFeatureItem,
  BtnPrimary,
} from '../../common/LandingPage.styled';
import ReaderImage from '../../../../assets/screenshots/screenshot-reader.jpg';

type ReaderFeatureKey = 'discovery' | 'bookmark' | 'support' | 'realtime';

interface ReaderFeatureDef {
  icon: ReactElement;
  key: ReaderFeatureKey;
}

const READER_FEATURES: readonly ReaderFeatureDef[] = [
  { icon: <ExploreIcon />, key: 'discovery' },
  { icon: <BookmarkIcon />, key: 'bookmark' },
  { icon: <VolunteerActivismIcon />, key: 'support' },
  { icon: <AccessTimeIcon />, key: 'realtime' },
] as const;

export function ForReadersSection() {
  const { t } = useTranslation();

  return (
    <Section alt id="for-readers">
      <Container>
        <ReaderSplit>
          <ReaderVisual>
            <div style={{ position: 'relative', display: 'inline-block' }}>
              <Box
                component="img"
                src={ReaderImage}
                alt={t('landing.forReaders.screenshotLabel')}
                sx={{
                  width: '100%',
                  maxHeight: 500,
                  objectFit: 'contain',
                  display: 'block',
                  borderRadius: '20px',
                }}
              />
              <ReaderFloat>
                <span style={{ fontSize: 24 }}>
                  <BookmarksIcon />
                </span>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 500, color: T.cream }}>
                    {t('landing.forReaders.currentlyReading')}
                  </div>
                  <div style={{ fontSize: 11, color: T.text3, marginTop: 2 }}>
                    {t('landing.forReaders.bookmarked', { count: 42 })}
                  </div>
                </div>
              </ReaderFloat>
            </div>
          </ReaderVisual>

          <div>
            <SectionEyebrow>{t('landing.forReaders.eyebrow')}</SectionEyebrow>
            <SectionTitle left>
              {t('landing.forReaders.title')}
              <br />
              <em>{t('landing.forReaders.titleEm')}</em>
            </SectionTitle>
            <SectionSub left style={{ marginBottom: 32 }}>
              {t('landing.forReaders.sub')}
            </SectionSub>
            <ReaderFeatureList>
              {READER_FEATURES.map(({ icon, key }) => (
                <ReaderFeatureItem key={key}>
                  <span className="icon">{icon}</span>
                  <div>
                    <strong>{t(`landing.forReaders.features.${key}.title`)}</strong>
                    <p>{t(`landing.forReaders.features.${key}.body`)}</p>
                  </div>
                </ReaderFeatureItem>
              ))}
            </ReaderFeatureList>
            <BtnPrimary to="/register">
              {t('landing.forReaders.cta')} <ArrowForwardIcon />
            </BtnPrimary>
          </div>
        </ReaderSplit>
      </Container>
    </Section>
  );
}

export default ForReadersSection;
